import useIsMobile from 'hooks/useIsMobile';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ScratchpadMessage } from 'shared';

import { ChatProps } from './index.types';

export function useChatController(props: ChatProps) {
  const { messages: _messages, onEdit, onNewMessage } = props;
  const isMobile = useIsMobile();
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const textFieldRef = useRef<HTMLDivElement>(null);

  const messages = useMemo(
    () =>
      Array.from(_messages || []).sort(
        (a, b) => new Date(a.created).getTime() - new Date(b.created).getTime(),
      ),
    [_messages],
  );

  const startEdit = useCallback((message: ScratchpadMessage) => {
    setEditingMessageId(message.id);
    setEditingContent(message.content);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingMessageId(null);
    setEditingContent('');
  }, []);

  const saveEdit = useCallback(async () => {
    if (!onEdit) return;
    if (!editingMessageId) return;

    const messageToEdit = messages?.find((m) => m.id === editingMessageId);
    if (!messageToEdit) return;

    setSending(true);
    try {
      await onEdit(messageToEdit, editingContent);
      setEditingMessageId(null);
      setEditingContent('');
    } finally {
      setSending(false);
    }
  }, [editingMessageId, editingContent, messages, onEdit]);

  const messagesWithMetadata = useMemo(() => {
    if (!messages?.length) return [];

    return messages.map((message, i) => {
      const previousMessage = i > 0 ? messages[i - 1] : null;
      const currentMessageMoment = moment(message.created);
      const previousMessageMoment = previousMessage
        ? moment(previousMessage.created)
        : null;

      const isSameDay = previousMessageMoment
        ? currentMessageMoment.isSame(previousMessageMoment, 'day')
        : false;

      const isPreviousMessageWithin30Minutes = previousMessageMoment
        ? currentMessageMoment.diff(previousMessageMoment, 'minutes') <= 30
        : false;

      return {
        message,
        currentMessageMoment,
        showDivider: !isSameDay,
        isPreviousMessageWithin30Minutes,
      };
    });
  }, [messages]);

  useEffect(() => {
    if (textFieldRef.current && !isMobile) {
      const func = (e: KeyboardEvent) => {
        const textarea = textFieldRef.current?.querySelector('textarea');
        if (!textarea) return;

        if (
          textarea !== document.activeElement &&
          document.activeElement?.tagName !== 'INPUT' &&
          document.activeElement?.tagName !== 'TEXTAREA'
        ) {
          if (
            e.key.charCodeAt(0) >= 32 &&
            e.key.charCodeAt(0) <= 126 &&
            !e.ctrlKey &&
            !e.metaKey &&
            !e.altKey &&
            !e.shiftKey
          ) {
            textarea.focus();
          }
        }
      };

      window.addEventListener('keydown', func);

      return () => {
        window.removeEventListener('keydown', func);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendMessage = useCallback(async () => {
    setSending(true);
    await onNewMessage(message);
    setMessage('');
    setSending(false);
  }, [message, onNewMessage]);

  return {
    messages,
    sending,
    message,
    setMessage,
    editingMessageId,
    editingContent,
    setEditingContent,
    textFieldRef,
    isMobile,
    messagesWithMetadata,
    startEdit,
    cancelEdit,
    saveEdit,
    sendMessage,
  };
}
